import { useCallback, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { TabBar, tabId, tabPanelId, type TabBarItem } from '@/components/TabBar'
import { ProjectSectionHeader } from '@/components/projects/ProjectSectionHeader'
import { DeleteVariableGroupDialog } from '@/components/projects/environment-variables/DeleteVariableGroupDialog'
import { VariableGroupDialog } from '@/components/projects/environment-variables/VariableGroupDialog'
import { VariableGroupsTab } from '@/components/projects/environment-variables/VariableGroupsTab'
import { VariableGroupUsageTab } from '@/components/projects/environment-variables/VariableGroupUsageTab'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { VariableGroupSummary } from '@/types'

type EnvironmentVariablesTab = 'groups' | 'usage'

const ENVIRONMENT_VARIABLES_TABS = 'environment-variables'

const tabs: TabBarItem<EnvironmentVariablesTab>[] = [
  { key: 'groups', label: 'Project Groups' },
  { key: 'usage', label: 'Usage History' },
]

export function EnvironmentVariablesPage() {
  const { projectId } = useParams()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const [searchParams, setSearchParams] = useSearchParams()
  const [dialog, setDialog] = useState<{ groupId?: string } | null>(null)
  const [deleting, setDeleting] = useState<VariableGroupSummary | null>(null)

  const activeTab: EnvironmentVariablesTab = searchParams.get('tab') === 'usage' ? 'usage' : 'groups'
  const canManage = hasPermission(Permissions.Project.VariableGroups.Manage, projectId)

  const handleTabChange = useCallback((tab: EnvironmentVariablesTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (tab === 'usage') next.set('tab', tab)
      else next.delete('tab')
      return next
    }, { replace: true })
  }, [setSearchParams])

  if (authLoading) {
    return null
  }

  if (!hasPermission(Permissions.Project.VariableGroups.Read, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  return (
    <div className="space-y-6">
      <ProjectSectionHeader sectionName="Environment Variables">
        {canManage && (
          <Button onClick={() => setDialog({})}>
            <Plus className="mr-2 h-4 w-4" />
            New Environment Variable Group
          </Button>
        )}
      </ProjectSectionHeader>

      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} className="w-fit" idPrefix={ENVIRONMENT_VARIABLES_TABS} />

      <div
        id={tabPanelId(ENVIRONMENT_VARIABLES_TABS)}
        role="tabpanel"
        aria-labelledby={tabId(ENVIRONMENT_VARIABLES_TABS, activeTab)}
        className="rounded-lg border bg-card p-6"
      >
        {activeTab === 'usage' ? (
          <VariableGroupUsageTab projectId={projectId!} />
        ) : (
          <VariableGroupsTab
            projectId={projectId!}
            canManage={canManage}
            onCreate={() => setDialog({})}
            onEdit={group => setDialog({ groupId: group.id })}
            onDelete={setDeleting}
          />
        )}
      </div>

      {canManage && (
        <VariableGroupDialog
          projectId={projectId!}
          open={!!dialog}
          onOpenChange={open => { if (!open) setDialog(null) }}
          groupId={dialog?.groupId}
        />
      )}

      {deleting && (
        <DeleteVariableGroupDialog
          projectId={projectId!}
          group={deleting}
          open={!!deleting}
          onOpenChange={open => { if (!open) setDeleting(null) }}
        />
      )}
    </div>
  )
}
